import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';

import { Router } from '@angular/router';
import { urlBuilder } from '@metromarkets/sdk';
import { environment } from '~env/environment';
import {
  APP_ALL_OFFERS_ROUTE,
  APP_CATALOG_ROUTE,
  APP_CATEGORY_ROUTE,
  APP_LANDING_PAGE_ROUTE,
  FeatureToggleEnum,
  NUMBER_0,
  NUMBER_1,
  NUMBER_2,
  NUMBER_9,
} from '~shared/constants';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Category } from '~app/modules/generic/models';
import { CategoriesService } from '~app/modules/generic/services/categories.service';
import { CategoriesNavbarApplicationEventHandler } from '~app/modules/generic/helpers';
import { CachedCategoriesUtil } from '~app/modules/generic/utils/cached-categories.util';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import * as fromStoryblok from '~app/modules/storyblok/store';
import { BuyerState } from '~app/modules/generic/store';
import { select, Store } from '@ngrx/store';
import { StoryData } from 'storyblok-js-client';
import {
  EditorialList,
  ThematicResources,
  TopMenuPromotionLinkBlok,
} from '~app/modules/storyblok/models/storyblok-global-content.model';
import { PROMO_CATEGORIES_ID, TOP_MENU_PROMOTION_LINK_ID } from '~app/modules/generic/constants/promo-constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Component({
  selector: 'app-mobile-categories',
  templateUrl: './mobile-categories.component.html',
  styleUrls: ['./mobile-categories.component.scss'],
  animations: [
    trigger('categoryListAnimation', [
      transition('true => false', [
        animate(
          '0.2s',
          style({
            transform: 'translateX(-100%)',
            opacity: 0,
          })
        ),
      ]),
      transition('false => true', [
        animate(
          '0.2s',
          style({
            transform: 'translateX(100%)',
            opacity: 0,
          })
        ),
      ]),
    ]),
  ],
})
export class MobileCategoriesComponent implements OnDestroy, OnChanges {
  @Input() categories: Category[];
  @Output() menuClosed = new EventEmitter<null>();
  @Output() categorySelected = new EventEmitter<Category>();
  @Output() hideTopLineMenu = new EventEmitter<boolean>();
  @Input() mobileMenuShown: boolean;

  marketplaceLink = environment.appBaseUrl();
  displayCategories: Category[];
  selectedCategory: Category;
  isTopCategoryView = true;
  levelOneCategories: Category[];
  levelOneCategory: Category;
  catLevelOne: number;
  catLevelCounter: number = NUMBER_0;
  parentCat: string;
  promoCategoriesId = PROMO_CATEGORIES_ID;
  topMenuPromotionLinkId = TOP_MENU_PROMOTION_LINK_ID;
  market;
  outdoorRoute;
  allOffersRoute = [`/${APP_LANDING_PAGE_ROUTE}/${APP_ALL_OFFERS_ROUTE}`];
  maxPromoMenuItems = NUMBER_9;
  isAllOffersFFEnabled: boolean;
  isReactPLPsEnabled: boolean;

  public editorialList: EditorialList;
  public thematicResources: ThematicResources;

  private applicationEventHandler: CategoriesNavbarApplicationEventHandler;
  private destroySubject$ = new Subject<void>();

  constructor(
    private categoriesService: CategoriesService,
    private router: Router,
    private applicationEventService: ApplicationEventService,
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private productsListUrlService: ProductsListUrlService,
    private store: Store<BuyerState>,
    private featureFlagService: ActiveFeaturesConfigcatService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    this.market = getCountryDomainCode(this.webAppBuyerRequest);
    this.applicationEventHandler = new CategoriesNavbarApplicationEventHandler(
      applicationEventService,
      this.translateService
    );

    this.featureFlagService
      .isFeatureEnabled(FeatureToggleEnum.FF_MACO_ALL_OFFERS)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((isEnabled) => {
        this.isAllOffersFFEnabled = isEnabled;
      });

    this.featureFlagService
      .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_PLP_REACT_MIGRATION)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((isEnabled: boolean): void => {
        this.isReactPLPsEnabled = isEnabled;
      });

    this.setOfferMenuContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.categories.currentValue.length) {
      this.isTopCategoryView = true;
      this.displayCategories = this.categories.filter((c) => !c.parents || !c.parents.length);
      this.setPromoCategories();
    }
  }

  setPromoCategories() {
    if (CachedCategoriesUtil.isCachedPromotionCategoriesExisted()) {
      this.setTopAngeboteMenu(CachedCategoriesUtil.getCachedPromotionCategories());
    } else {
      this.categoriesService
        .getPromoCategories()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe((response: Array<Category>) => {
          this.setTopMenuPromotionLinkNavigationMenu();
          if (response.length > NUMBER_0) {
            this.setTopAngeboteMenu(response);
          }
        });
    }
  }

  setOfferMenuContent(): void {
    this.store
      .pipe(select(fromStoryblok.getOfferMenuContent), takeUntil(this.destroySubject$))
      .subscribe((offerMenuContent: StoryData) => {
        if (offerMenuContent?.content?.global?.length) {
          this.editorialList = offerMenuContent?.content?.global?.[0].editoriallist?.[0];
          this.thematicResources = offerMenuContent?.content?.global?.[0].thematicresources?.[0];
        }
      });
  }

  setTopAngeboteMenu(promoCategories: Category[]) {
    this.translateService
      .get('HOME.HOME_PAGE.PROMO_CATEGORIES.PARENT_CATEGORY_TITLE')
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((translation) => {
        const promoCategory = {
          id: PROMO_CATEGORIES_ID,
          name: translation,
          parents: [],
          children: promoCategories,
          slug: '',
        };
        // Add promotion category to both the display and categories
        this.categories = [promoCategory, ...this.categories];
        this.displayCategories = [promoCategory, ...this.displayCategories];
        this.changeDetectorRef.detectChanges();
      });
  }

  setTopMenuPromotionLinkNavigationMenu() {
    this.store
      .pipe(select(fromStoryblok.getNavigation), takeUntil(this.destroySubject$))
      .subscribe((navigationStory: StoryData) => {
        const topMenuPromotionLink: TopMenuPromotionLinkBlok = navigationStory?.content?.global?.[0];
        if (topMenuPromotionLink) {
          const angeboteIndexInCategoriesList = this.getCategoryItemIndex(this.categories);
          const angeboteIndexInDisplayedCategoriesList = this.getCategoryItemIndex(this.displayCategories);
          const topMenuPromotionLinkCategory = {
            id: TOP_MENU_PROMOTION_LINK_ID,
            children: [],
            parents: [],
            slug: topMenuPromotionLink.link,
            name: topMenuPromotionLink.title,
            storyBlokEditable: topMenuPromotionLink._editable,
          };
          this.categories.splice(angeboteIndexInCategoriesList + NUMBER_1, NUMBER_0, topMenuPromotionLinkCategory);
          this.displayCategories.splice(
            angeboteIndexInDisplayedCategoriesList + NUMBER_1,
            NUMBER_0,
            topMenuPromotionLinkCategory
          );
        }
      });
  }

  onCategorySelect(category: Category, label?: string) {
    if (!this.isTopCategoryView) {
      if (category.id === PROMO_CATEGORIES_ID) {
        this.navigateToPromoPage();
      } else {
        this.navigateToCategoryPage(category);
      }
      this.closeMenu();
    } else if (category.id === TOP_MENU_PROMOTION_LINK_ID) {
      this.navigateToTopMenuPromotionLink(category);
    } else {
      this.hideTopLineMenu.emit(true);
    }

    const child = this.selectedCategory?.children.length > 0;
    let parentName;
    if (child) {
      parentName = this.selectedCategory.name;
    }
    this.productsListUrlService.subCategoryId = category.id;
    this.fireNavigationTracking(category, parentName, label);
    this.selectedCategory = category;
    this.displayCategories = this.selectedCategory.children.slice();
    this.isTopCategoryView = false;
  }

  fireAllPromotionsClickEvent(selectedCategoryName: string, label?: string): void {
    this.applicationEventHandler.publishAppNavigation(selectedCategoryName, label);
    this.closeMenu();
    this.applicationEventHandler.publishSeeAllPromotion();
  }

  navigateToCategory(category: Category, label?: string) {
    this.closeMenu();
    if (category.id === PROMO_CATEGORIES_ID) {
      this.navigateToPromoPage();
    } else {
      this.navigateToCategoryPage(category);
    }
  }

  oneLevelBackCategories() {
    if (this.catLevelCounter === NUMBER_2) {
      this.catLevelOne = 1;
      this.catLevelCounter = this.catLevelCounter - NUMBER_1;
      this.displayCategories = this.levelOneCategories.filter((c) => !c.parents || !c.parents.length);
      this.selectedCategory = this.levelOneCategory;
      this.isTopCategoryView = false;
    } else {
      this.catLevelCounter = NUMBER_0;
      this.catLevelOne = 0;
      this.displayCategories = this.categories.filter((c) => !c.parents || !c.parents.length);
      this.isTopCategoryView = true;
    }
    this.hideTopLineMenu.emit(false);
  }

  backToTopCategories() {
    this.displayCategories = this.categories.filter((c) => !c.parents || !c.parents.length);
    this.hideTopLineMenu.emit(false);
    this.isTopCategoryView = true;
    this.selectedCategory = null;
  }

  closeMenu() {
    this.menuClosed.emit();
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  private navigateToPromoPage(): void {
    if (!this.isAllOffersFFEnabled) {
      if (!this.isReactPLPsEnabled) {
        this.router.navigate([APP_CATALOG_ROUTE], {
          queryParams: urlBuilder.encode({
            filter: { promotion: true },
          }),
        });
      } else {
        window.location.href = `${environment.appBaseUrl()}/${APP_CATALOG_ROUTE}?f.promotion=true`;
      }

      return;
    }

    this.router.navigate(this.allOffersRoute);
  }

  private navigateToCategoryPage(category: Category): void {
    const isPromo = this.selectedCategory.id === PROMO_CATEGORIES_ID;
    this.router.navigate([APP_CATEGORY_ROUTE, category.slug], {
      queryParams: urlBuilder.encode({
        filter: {
          promotion: isPromo ? true : null,
        },
      }),
    });
  }

  private navigateToTopMenuPromotionLink(category: Category) {
    this.closeMenu();
    window.location.href = this.marketplaceLink + category.slug;
  }

  private fireNavigationTracking(category: Category, parentName?: string, label?: string) {
    if (label) {
      this.applicationEventHandler.publishAppNavigation(category.name, label);
    } else if (parentName) {
      this.applicationEventHandler.publishAppNavigation(`${parentName}/${category.name}`);
    } else {
      this.applicationEventHandler.publishAppNavigation(category.name);
    }
  }

  private getCategoryItemIndex(categoryArray: Category[]): number {
    return categoryArray.indexOf(categoryArray.find((category: Category) => category.id === PROMO_CATEGORIES_ID));
  }
}
